/**
 * @fileoverview DXY Trend Signal
 * Computes trend direction and percentage change from DXY time series.
 * @module signals/liquidity/dxy_trend
 *
 * CONTRACT:
 * - Input: { series: TimeseriesPoint[] } where series is ordered oldest to newest
 * - Output: DxyTrendOutput with trend direction, pctChange, start/end values
 * - Errors: Throws if series is invalid (empty, malformed, or contains NaN)
 * - Behavior: Pure function, deterministic output for same input
 */

import { isValidSeries, createSignalValidationError } from "../types.js";

/**
 * Input for the DXY trend signal.
 * @typedef {Object} DxyTrendInput
 * @property {import('../types.js').TimeseriesPoint[]} series - DXY time series, ordered oldest to newest
 */

/**
 * Output from the DXY trend signal.
 * @typedef {Object} DxyTrendOutput
 * @property {'up' | 'down' | 'flat'} trend - Trend direction based on threshold
 * @property {number} pctChange - Percentage change from start to end (rounded to 2 decimals)
 * @property {number} startValue - First value in the series
 * @property {number} endValue - Last value in the series
 */

/**
 * Threshold for determining flat trend (in percentage points).
 * @type {number}
 */
const FLAT_THRESHOLD = 0.5;

/**
 * Computes the trend direction based on percentage change.
 * @param {number} pctChange - Percentage change
 * @returns {'up' | 'down' | 'flat'} Trend direction
 */
function determineTrend(pctChange) {
  if (pctChange > FLAT_THRESHOLD) return "up";
  if (pctChange < -FLAT_THRESHOLD) return "down";
  return "flat";
}

/**
 * DXY Trend Signal Definition.
 *
 * Measures the directional movement of the US Dollar Index over the input series.
 * A rising DXY indicates dollar strength, which typically creates stress for
 * offshore USD borrowers.
 *
 * @type {import('../types.js').SignalDefinition<DxyTrendInput, DxyTrendOutput>}
 */
export const dxyTrendSignal = {
  id: "dxy_trend",
  name: "DXY Trend",
  description:
    "Measures the directional movement of the US Dollar Index over the specified window. " +
    "Returns trend direction (up/down/flat) and percentage change.",
  category: "liquidity",

  /**
   * Computes DXY trend from time series.
   *
   * @param {DxyTrendInput} input - Input containing DXY series
   * @returns {DxyTrendOutput} Computed trend output
   * @throws {Error} If series is invalid (empty, malformed, or contains NaN values)
   *
   * @example
   * const result = dxyTrendSignal.compute({
   *   series: [
   *     { t: "2024-01-01", v: 100 },
   *     { t: "2024-12-31", v: 105 }
   *   ]
   * });
   * // result: { trend: "up", pctChange: 5, startValue: 100, endValue: 105 }
   */
  compute(input) {
    const { series } = input;

    if (!isValidSeries(series)) {
      throw new Error(
        createSignalValidationError(
          "dxy_trend",
          "Series must be a non-empty array of valid { t, v } points with no NaN values"
        )
      );
    }

    const startValue = series[0].v;
    const endValue = series[series.length - 1].v;

    // Handle edge case: startValue is zero (would cause division by zero)
    if (startValue === 0) {
      throw new Error(
        createSignalValidationError(
          "dxy_trend",
          "Start value cannot be zero (division by zero)"
        )
      );
    }

    const pctChange = ((endValue - startValue) / startValue) * 100;
    const trend = determineTrend(pctChange);

    return {
      trend,
      pctChange: Math.round(pctChange * 100) / 100,
      startValue,
      endValue,
    };
  },
};
